import { useState, useEffect, useCallback } from 'react';
import portfolioService from '../services/portfolioService';
import { toast } from 'sonner';

const usePortfolio = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchPortfolios = useCallback(async (searchQuery = '') => {
    try {
      setLoading(true);
      const data = await portfolioService.getPortfolios(searchQuery);
      setPortfolios(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch portfolios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchPortfolios(search);
    }, 300);
    return () => clearTimeout(handler);
  }, [search, fetchPortfolios]);

  const addPortfolio = async (formData) => {
    try {
      await portfolioService.createPortfolio(formData);
      toast.success('Portfolio created successfully');
      fetchPortfolios(search);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create portfolio');
      return false;
    }
  };

  const updatePortfolio = async (id, formData) => {
    try {
      await portfolioService.updatePortfolio(id, formData);
      toast.success('Portfolio updated successfully');
      fetchPortfolios(search);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update portfolio');
      return false;
    }
  };

  const deletePortfolio = async (id) => {
    try {
      await portfolioService.deletePortfolio(id);
      toast.success('Portfolio deleted successfully');
      fetchPortfolios(search);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete portfolio');
      return false;
    }
  };

  const getPortfolioById = useCallback(async (id) => {
    try {
      return await portfolioService.getPortfolioById(id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load project details');
      return null;
    }
  }, []);

  const reorderPortfolios = async (reorderedItems) => {
    try {
      setPortfolios(reorderedItems);
      const payload = reorderedItems.map((item, index) => ({
        id: item.id,
        priority: index + 1
      }));
      await portfolioService.reorderPortfolios(payload);
      toast.success('Portfolios reordered successfully');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reorder portfolios');
      fetchPortfolios(search);
      return false;
    }
  };

  return {
    portfolios,
    loading,
    addPortfolio,
    updatePortfolio,
    deletePortfolio,
    getPortfolioById,
    reorderPortfolios,
    search,
    setSearch,
    refresh: fetchPortfolios,
  };
};

export default usePortfolio;
